/**
 * SectionBand — the sanctioned SECTION band (Peace Design Language v1,
 * structure tier). Page anatomy is nav → band → body, no exceptions:
 * every top-level page except Home wears this short forest band. (Home
 * keeps its taller HERO band — greeting, quote, watermark — in Hub.tsx;
 * that band is protected and does not compose this primitive.)
 *
 * The band IS the page's display register: eyebrow (letterspaced caps,
 * gold), serif display title (the display type step — equal page rank,
 * equal step), ONE purpose line in guide voice, page search (search-first
 * pages only), and the page's ONE primary action top right.
 *
 * Founder walk (2026-07-06, production screenshots): the band tightened
 * vertically (py-5/md:py-6, was py-7/md:py-9 — tall for its content) and
 * the purpose line runs at the band's CONTENT width (single line on
 * desktop; the old 560px cap wrapped it into a narrow column). The
 * purpose is BAND content per DL1 — pages must not orphan it into the
 * body below.
 *
 * THE ONE WATERMARK SPEC (codified from Amanda's 2026-07-03 banner
 * ruling; supersedes the per-page hand-rolls): the tenant's mark spans
 * the band's height, aspect preserved, slid half off the content frame's
 * right edge, cream at 5% on forest. Pages do not pass their own
 * watermark classes — the spec lives here so it cannot drift.
 *
 * Color note: the band is FOREST (bg-sage-800) per the adopted DL v1
 * ruling ("SECTION band — short, forest, always"). Band COLOR migration
 * of the pre-existing sage-400 bands (AI Tools, Directory) belongs to
 * the strings-tier tranche; new bands start on this primitive.
 *
 * Contract: the band is full-bleed; the page's BODY provides its own
 * container (max-w-page-narrow / -standard / -wide per DL9). The band's
 * inner frame takes the SAME DL9 width token + gutter as the page body
 * (founder walk 2026-07-06, alignment hotfix: "the band's content column
 * and the page body's content column share ONE left edge, per page" —
 * the old fixed 1160px frame disagreed with every body width). Pages
 * pass `width` matching their body container; the gutter is the shared
 * PAGE_GUTTER token, never a band-private padding.
 */

import { type ReactNode } from 'react';
import { BrandWatermark } from '@/components/primitives/BrandWatermark';
import { PAGE_GUTTER, PAGE_WIDTH_CLASSES } from '@/components/primitives/PageShell';

export type SectionBandWidth = 'narrow' | 'standard' | 'wide';

export interface SectionBandProps {
  /** Letterspaced-caps kicker, e.g. "Olive · Work". */
  eyebrow: string;
  /** Serif display title (the h1 — one per page). */
  title: string;
  /** Controls beside the title, wrapping underneath it on a phone. */
  headingControls?: ReactNode;
  /** ONE purpose line, guide voice. */
  purpose?: string;
  /** A test hook on the purpose line (My Clients' counts line). */
  purposeTestId?: string;
  /** Page search slot — search-first pages only. */
  search?: ReactNode;
  /** The page's ONE primary action, top right. */
  action?: ReactNode;
  /**
   * DL9 width of the band's content frame — MUST match the page body's
   * container width so band and body share one left edge (defaults to
   * 'standard', the most common body width).
   */
  width?: SectionBandWidth;
  testId?: string;
}

export function SectionBand({
  eyebrow,
  title,
  headingControls,
  purpose,
  purposeTestId,
  search,
  action,
  width = 'standard',
  testId,
}: SectionBandProps): ReactNode {
  return (
    <section data-testid={testId} className="relative bg-sage-800 text-cream">
      {/* No overflow-hidden on the band itself: a search slot's dropdown
          must escape it. The watermark takes its own clip layer (the
          ClientsHome band pattern). */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <BrandWatermark
          fit="height"
          className="inset-y-0 right-0 translate-x-[45%] text-cream/[5%]"
        />
      </div>
      <div
        data-testid={testId ? `${testId}-frame` : undefined}
        className={`relative mx-auto ${PAGE_WIDTH_CLASSES[width]} ${PAGE_GUTTER} py-5 md:py-6`}
      >
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
          <div className="min-w-0">
            <div className="mb-3 inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.16em] text-warm-400">
              <span aria-hidden="true" className="h-px w-[22px] bg-warm-400" />
              {eyebrow}
            </div>
            {headingControls ? (
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-6 gap-y-3">
                <h1 className="font-display text-[clamp(1.875rem,3.2vw,2.625rem)] font-semibold tracking-[-0.01em] text-cream">
                  {title}
                </h1>
                <div className="flex min-w-0 max-w-full basis-full flex-wrap items-baseline gap-3 sm:basis-auto">
                  {headingControls}
                </div>
              </div>
            ) : (
              <h1 className="font-display text-[clamp(30px,3.2vw,42px)] font-semibold tracking-[-0.01em] text-cream">
                {title}
              </h1>
            )}
            {purpose && (
              <p className="mt-2 text-[14.5px] leading-snug text-cream/85" data-testid={purposeTestId}>
                {purpose}
              </p>
            )}
          </div>
          {(search || action) && (
            <div className="flex min-w-0 flex-wrap items-center gap-3 pb-1">
              {search}
              {action}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
